import { calendarDateFromHash, parseCalendarDate } from '@util/CalendarEmbed.utilities';
import React, { KeyboardEvent, useState } from 'react';
import {
	MarketStateSnapshotCalendarHeader,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/fragment/MarketStateSnapshotCalendarHeader';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import CalendarDay from '@app/state/calendar/CalendarDay';
import {
	MarketStateSnapshotCalendarDay,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/fragment/MarketStateSnapshotCalendarDay';
import {
	MarketStateSnapshotCalendarDayForMobile,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_mobile/fragment/MarketStateSnapshotCalendarDayForMobile';
import {
	MarketStateSnapshotCalendarDayTableForMobile,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_mobile/fragment/MarketStateSnapshotCalendarDayTableForMobile';
import {
	MarketStateDailySnapshotTooltip,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/MarketStateDailySnapshotTooltip';
import {
	useMarketStateSnapshotCalendarKeyboardNavigation,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/hook/useMarketStateSnapshotCalendarKeyboardNavigation';
import Utilities from '@util/Utilities';
import MarketAreaStateForMonthProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/MarketAreaStateForMonthProvider';
import MarketStateNotificationsForMonthProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/MarketStateNotificationsForMonthProvider';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import PricingRoomsInventoryProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/PricingRoomsInventoryProvider';
import { useLocation } from 'react-router-dom';


const MarketStateSnapshotCalendarPage = React.memo(() => {
	const tools = useMarketStateTools();

	const rows = Math.floor(tools.calendarMonth.days.length / 7);
	const [selectedCalendarDay, setSelectedCalendarDay] = useState<CalendarDay | undefined>(undefined);
	const [tooltipOpenRequestId, setTooltipOpenRequestId] = useState(0);
	const selectedCalendarDayDisplay = React.useRef<string | undefined>(undefined);
	const location = useLocation();
	const handledNavigation = React.useRef<string | undefined>(undefined);
	const { companyHotel } = useSelectedCompanyHotel();

	const setSelectedCalendarDayWithTooltip = (calendarDay: CalendarDay | undefined) => {
		if (selectedCalendarDayDisplay.current !== calendarDay?.display) {
			selectedCalendarDayDisplay.current = calendarDay?.display;
			if (calendarDay) setTooltipOpenRequestId(value => value + 1);
		}
		setSelectedCalendarDay(calendarDay);
	};

	const onSelectCalendarDay = (calendarDay: CalendarDay | undefined) => {
		setHideToolbar(false);
		setSelectedCalendarDayWithTooltip(calendarDay);
	};
	const calendarNavigation = useMarketStateSnapshotCalendarKeyboardNavigation();
	const [hideToolbar, setHideToolbar] = React.useState(false);

	React.useEffect(() => {
		const navigationId = `${location.key}:${location.hash}:${companyHotel.id}`;
		if (handledNavigation.current === navigationId) return;
		const selectedStatDate = calendarDateFromHash(location.hash, companyHotel.id)
			?? parseCalendarDate((location.state as { selectedStatDate?: string } | null)?.selectedStatDate);
		if (!selectedStatDate) return;
		const year = Number(selectedStatDate.slice(0, 4));
		const month = Utilities.getMonthByIndex(Number(selectedStatDate.slice(5, 7)) - 1);
		if (tools.calendarMonth.index.year !== year || tools.calendarMonth.index.month !== month) {
			tools.setCalendarMonth({ year, month });
			return;
		}
		const calendarDay = tools.calendarMonth.days.find(day => day.display === selectedStatDate);
		if (!calendarDay) return;
		handledNavigation.current = navigationId;
		setSelectedCalendarDayWithTooltip(calendarDay);
		const calendarId = window.matchMedia('(min-width: 1024px)').matches
			? `calendar-day-desktop-${selectedStatDate}`
			: `calendar-day-mobile-${selectedStatDate}`;
		requestAnimationFrame(() => document.getElementById(calendarId)
			?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
	}, [location.key, location.hash, companyHotel.id, tools.calendarMonth.days]);

	const navigate = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.code === 'ArrowRight') {
			setHideToolbar(false);
			calendarNavigation.nextDay(selectedCalendarDay, setSelectedCalendarDayWithTooltip, tools.calendarMonth);
		} else if (e.code === 'ArrowLeft') {
			setHideToolbar(false);
			calendarNavigation.previousDay(selectedCalendarDay, setSelectedCalendarDayWithTooltip, tools.calendarMonth);
		} else if (e.code === 'ArrowDown') {
			setHideToolbar(false);
			calendarNavigation.nextWeek(selectedCalendarDay, setSelectedCalendarDayWithTooltip, tools.calendarMonth);
		} else if (e.code === 'ArrowUp') {
			setHideToolbar(false);
			calendarNavigation.previousWeek(selectedCalendarDay, setSelectedCalendarDayWithTooltip, tools.calendarMonth);
		} else if (e.code === 'Space') {
			if (selectedCalendarDay) {
				setHideToolbar(false);
				setTooltipOpenRequestId(value => value + 1);
			}
		}
		e.preventDefault();
	};

	return <MarketStateNotificationsForMonthProvider companyHotel={companyHotel} calendarMonth={tools.calendarMonth}>
		<MarketAreaStateForMonthProvider companyHotel={companyHotel} calendarMonth={tools.calendarMonth}>
			<PricingRoomsInventoryProvider companyHotel={companyHotel}>
				<div className="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
					<MarketStateSnapshotCalendarHeader />
					<div
						id="calendar"
						className="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto"
						onKeyDown={navigate}
					>
						<div
							className={`hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-${rows} lg:gap-px`}>
								{tools.marketStateDailySnapshotStates.map((dailySnapshotState, index) =>
									<MarketStateSnapshotCalendarDay
										index={index}
										key={`calendar_day_${dailySnapshotState.calendarDay.display}`}
										marketStateDailySnapshotState={dailySnapshotState}
										selectedCalendarDay={selectedCalendarDay}
										setSelectedCalendarDay={setSelectedCalendarDayWithTooltip}
									/>)}
						</div>
						<div
							className={`lg:hidden isolate grid w-full grid-cols-7 grid-rows-${rows} gap-px`}>
								{tools.marketStateDailySnapshotStates.map((dailySnapshotState) =>
									<MarketStateSnapshotCalendarDayForMobile
										key={`calendar_day_${dailySnapshotState.calendarDay.display}`}
										marketStateDailySnapshotState={dailySnapshotState}
										selectedCalendarDay={selectedCalendarDay}
										setSelectedCalendarDay={setSelectedCalendarDayWithTooltip}
									/>)}
						</div>
					</div>
				</div>
				{!!selectedCalendarDay && <>
					<div className="mt-8 mx-2 lg:hidden">
						<MarketStateSnapshotCalendarDayTableForMobile
							selectedCalendarDay={selectedCalendarDay}
							setSelectedCalendarDay={onSelectCalendarDay}
						/>
					</div>
					<div className={Utilities.classNames(!hideToolbar ? 'hidden lg:block' : 'hidden')}>
						{tools.marketStateDailySnapshotStates.map((dailySnapshotState) =>
							<MarketStateDailySnapshotTooltip
								key={`tooltip_calendar_${dailySnapshotState.calendarDay.display}`}
								tooltipId={'tooltip_calendar_' + dailySnapshotState.calendarDay.display}
								calendarDay={dailySnapshotState.calendarDay}
								place="right"
								openRequestId={tooltipOpenRequestId}
								shouldLoad={selectedCalendarDay.display === dailySnapshotState.calendarDay.display}
							/>)}
					</div>
				</>}
			</PricingRoomsInventoryProvider>
		</MarketAreaStateForMonthProvider>
	</MarketStateNotificationsForMonthProvider>;
});

export default MarketStateSnapshotCalendarPage;
